import { FC, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { Modal } from 'react-bootstrap';

import { ScreenSizes } from 'constants/sizes';

import Button from 'modules/_shared/components/Buttons';

import { setModal } from 'modules/_shared/redux/modalActions';
import { Council, Document } from 'types';
import { State } from 'redux/initialState';
import CustomLoading from 'modules/_shared/components/CustomLoading';
import { addAlert } from 'modules/_shared/redux/alertActions';
import alertBodyTypes from 'modules/_shared/components/Alert/alertBodyTypes';
import PDFViewer from 'modules/_shared/components/PDFViewer';
import UploadDocumentsSection from 'modules/documents/components/UploadDocumentsSection';
import { addCouncilDocuments } from 'modules/administratives/utils/addCouncilDocuments';
import { store } from 'redux/store';
import { updateCouncil } from 'modules/administratives/redux/councilActions';
import { PDF_TYPE_FILE_ALLOWED } from 'constants/defaultConstants';

type EditCouncilAnnouncementUploadProps = {
  size?: ScreenSizes;
  council: Council;
};

const EditCouncilAnnouncementUpload: FC<EditCouncilAnnouncementUploadProps> = ({
  council,
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();

  const user = useSelector((state: State) => state.user);
  const society = useSelector((state: State) => state.society.actualSociety);

  const [isLoading, setIsLoading] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<
    string | null
  >(null);

  const handlePreviewDocument = (index: number) => {
    const currentDocument = attachments[index];
    if (currentDocument) {
      const url = URL.createObjectURL(currentDocument);
      setAttachmentPreviewUrl(url);
    } else {
      if (attachmentPreviewUrl) {
        URL.revokeObjectURL(attachmentPreviewUrl);
      }
      setAttachmentPreviewUrl(null);
    }
  };

  // Auto-preview the first document when attachments change
  useEffect(() => {
    if (attachments.length > 0) {
      // Clean up previous preview URL
      if (attachmentPreviewUrl) {
        URL.revokeObjectURL(attachmentPreviewUrl);
      }
      // Preview the first document
      const firstDocument = attachments[0];
      const url = URL.createObjectURL(firstDocument);
      setAttachmentPreviewUrl(url);
    } else if (attachmentPreviewUrl) {
      // Clean up preview URL when no attachments
      URL.revokeObjectURL(attachmentPreviewUrl);
      setAttachmentPreviewUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachments]);

  const handleClose = () => {
    if (attachmentPreviewUrl) {
      URL.revokeObjectURL(attachmentPreviewUrl);
    }
    dispatch(setModal(null));
  };

  const handleSave = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const [uploadedAttachment] = await addCouncilDocuments({
        files: attachments,
        user,
        society,
      });

      const newCouncilData: Partial<Council> = {
        _id: council._id,
        announcementDocument: uploadedAttachment._id,
        announcementTemplate: null,
      };

      await store.dispatch(updateCouncil(newCouncilData));
      await store.dispatch(addAlert(alertBodyTypes.COUNCIL_UPDATED));

      dispatch(setModal(null));
    } catch (error) {
      await store.dispatch(addAlert(alertBodyTypes.ERROR_UPDATING_COUNCIL));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Modal.Header>
        <h4 className="modal-title mb-0">
          <span>{t('ReplaceCouncilAnnouncement')}</span>
        </h4>

        <a className="close cursor-pointer" onClick={handleClose}>
          <em className="icon ni ni-cross" />
        </a>
      </Modal.Header>

      <Modal.Body className="custom-modal-body">
        <div className="nk-modal">
          <UploadDocumentsSection
            title={t('Attachment')}
            initialDocumentsCount={0}
            documents={attachments as unknown as Document[]}
            setDocuments={setAttachments}
            onPreviewDocument={handlePreviewDocument}
            hideColumnHeaders
            hasPreview={false}
            multiple={false}
            accept={PDF_TYPE_FILE_ALLOWED}
          />

          {/* PDF Preview */}
          <div className="mt-3">
            <div
              className="border rounded p-0 overflow-hidden"
              style={{
                height: '330px',
                overflow: 'auto',
                backgroundColor: '#f8f9fa',
              }}
            >
              {attachmentPreviewUrl ? (
                <PDFViewer
                  file={attachmentPreviewUrl}
                  width={400}
                  height={300}
                />
              ) : (
                <div
                  className="d-flex align-items-center justify-content-center h-100"
                  style={{ color: '#6c757d' }}
                >
                  <div className="text-center">
                    <em
                      className="icon ni ni-file-pdf"
                      style={{ fontSize: '3rem' }}
                    />
                    <p className="mt-2 mb-0">{t('NoFileSelected')}</p>
                    <small>{t('UploadPDFToPreview')}</small>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isLoading && <CustomLoading />}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={attachments.length === 0}
        >
          {t('Save')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default EditCouncilAnnouncementUpload;
